import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import ProductSlider from '../components/ProductSlider';
import DOMPurify from 'dompurify';
import { FaFacebook, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import { useGlobalShippingRates } from '../hooks/useGlobalShippingRates';
import { useEventTracking } from '../hooks/useAnalytics';
import { useSellerAnalytics } from '../hooks/useSellerAnalytics';
import SEOMetadata from '../components/SEOMetadata';
import { ProductPageSkeleton } from '../components/skeleton';
import { generateProductSEOMetadata } from '../config/seo';

const ProductPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { trackProductView } = useEventTracking();
  const { trackProductView: trackBusinessProductView } = useSellerAnalytics();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [productNotFound, setProductNotFound] = useState(false);
  
  // Global shipping rates hook
  const { getShippingRate, loadShippingRates, loading: shippingLoading } = useGlobalShippingRates();
  
  const MAX_DESC_LENGTH = 400;
  const descriptionText = product?.description || '';
  const isLong = descriptionText.replace(/<[^>]+>/g, '').length > MAX_DESC_LENGTH;
  const shortDescription = descriptionText.replace(/<[^>]+>/g, '').slice(0, MAX_DESC_LENGTH);

  // Zoom immagine prodotto
  const [zoomOpen, setZoomOpen] = useState(false);

  // EAN da mostrare
  let eanToShow = null;
  if (selectedVariant && selectedVariant.ean) {
    eanToShow = selectedVariant.ean;
  } else if (variants.length > 0 && variants[0].ean) {
    eanToShow = variants[0].ean;
  }

  // Funzione helper per aggiungere i parametri UTM
  const addUtmParams = (url) => {
    if (!url) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}utm_source=loverlist&utm_medium=CSS&utm_campaign=product_page`;
  };

  // Funzione per calcolare la percentuale di sconto
  const calculateDiscount = (price, comparePrice) => {
    if (!comparePrice || comparePrice <= price) return null;
    return Math.round(((comparePrice - price) / comparePrice) * 100);
  };

  // Funzione per formattare i prezzi
  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    return Number(price).toFixed(2);
  };

  // Funzione per formattare le tariffe di spedizione
  const formatShippingInfo = (variantId) => {
    const shippingRate = getShippingRate(variantId);
    
    if (shippingLoading) {
      return <span className="product-shipping-loading">Caricamento spedizione...</span>;
    }

    if (!shippingRate) {
      return <span className="product-shipping-unavailable">Caricamento spedizione...</span>;
    }

    if (!shippingRate.has_rates) {
      return <span className="product-shipping-unavailable">Nessuna tariffa di spedizione disponibile</span>;
    }

    if (!shippingRate.shipping_rate) {
      return <span className="product-shipping-unavailable">Il venditore non spedisce in questo paese</span>;
    }

    const { shipping_cost } = shippingRate.shipping_rate;
    
    if (shipping_cost === 0) {
      return <span className="product-shipping-free">Spedizione gratuita</span>;
    }

    return (
      <span className="product-shipping-cost">
        €{shipping_cost.toFixed(2)}
      </span>
    );
  };

  // Funzione per verificare se l'opzione è un colore
  const isColorOption = (optionName) => {
    const colorNames = ['color', 'colore', 'colour', 'colori', 'colors'];
    return colorNames.includes(optionName.toLowerCase());
  };

  // Funzione per ottenere valori unici per ogni opzione
  const getUniqueOptionValues = (variants, optionName) => {
    const values = variants.map(v => 
      v.options.find(opt => opt.option_name === optionName)?.option_value
    ).filter(Boolean);
    return [...new Set(values)];
  };

  // Funzione per verificare se una combinazione di opzioni è disponibile
  const isCombinationAvailable = (optionName, value) => {
    return variants.some(variant => {
      // Verifica che tutte le opzioni selezionate siano presenti in questa variante
      return Object.entries(selectedOptions).every(([name, selectedValue]) => {
        if (name === optionName) return true; // Salta l'opzione che stiamo verificando
        const variantValue = variant.options.find(opt => opt.option_name === name)?.option_value;
        return variantValue === selectedValue;
      });
    });
  };

  // Funzione per ottenere l'immagine per un colore specifico
  const getColorImage = (color) => {
    const variant = variants.find(v => 
      v.options.find(opt => opt.option_name === 'Colore')?.option_value === color
    );
    return variant?.variant_image;
  };

  // Funzione per trovare la variante che corrisponde alle opzioni selezionate
  const findVariantByOptions = (options) => {
    return variants.find(variant => 
      Object.entries(options).every(([name, value]) => {
        const variantValue = variant.options.find(opt => opt.option_name === name)?.option_value;
        return variantValue === value;
      })
    );
  };

  // Funzione per aggiornare l'URL con il parametro variant
  const updateUrlWithVariant = (variantId) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (variantId) {
      newSearchParams.set('variant', variantId);
    } else {
      newSearchParams.delete('variant');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Funzione per raggruppare le varianti per option_name
  const groupVariantsByOption = (variants) => {
    const optionNames = [...new Set(variants.flatMap(v => v.options.map(o => o.option_name)))];
    
    return optionNames.reduce((acc, optionName) => {
      acc[optionName] = getUniqueOptionValues(variants, optionName);
      return acc;
    }, {});
  };

  // Logica prezzi/sconto coerente con ProductCard
  let priceDisplay = null;
  let compareAtDisplay = null;
  let discount = null;

  if (selectedVariant) {
    // Variante selezionata
    priceDisplay = <span className="product-price">{formatPrice(selectedVariant.price)}€</span>;
    if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
      compareAtDisplay = <span className="product-compare-price">{formatPrice(selectedVariant.compare_at_price)}€</span>;
      discount = calculateDiscount(selectedVariant.price, selectedVariant.compare_at_price);
    }
  } else if (product) {
    // Nessuna variante selezionata e product esiste
    if (product.price_min !== undefined && product.price_max !== undefined && parseFloat(product.price_min) !== parseFloat(product.price_max)) {
      // Range di prezzo
      priceDisplay = <span className="product-price">da {formatPrice(product.price_min)}€</span>;
      if (product.compare_at_price_min && parseFloat(product.compare_at_price_min) > parseFloat(product.price_min)) {
        compareAtDisplay = <span className="product-compare-price">{formatPrice(product.compare_at_price_min)}€</span>;
        discount = calculateDiscount(product.price_min, product.compare_at_price_min);
      }
    } else {
      // Prezzo singolo
      priceDisplay = <span className="product-price">{formatPrice(product.price_min)}€</span>;
      if (product.compare_at_price_min && parseFloat(product.compare_at_price_min) > parseFloat(product.price_min)) {
        compareAtDisplay = <span className="product-compare-price">{formatPrice(product.compare_at_price_min)}€</span>;
        discount = calculateDiscount(product.price_min, product.compare_at_price_min);
      }
    }
  }

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`/api/products/${id}`);
        setProduct(response.data.product);
        setVariants(response.data.variants);
        setSellers(response.data.sellers);
        
        if (response.data.variants.length > 0) {
          // Controlla se c'è un parametro variant nell'URL
          const variantParam = searchParams.get('variant');
          let targetVariant = null;
          
          if (variantParam) {
            // Cerca la variante specifica nell'array delle varianti
            targetVariant = response.data.variants.find(variant => variant.id === variantParam);
          }
          
          // Se non trova la variante specifica o non c'è il parametro, usa la prima variante
          if (!targetVariant) {
            targetVariant = response.data.variants[0];
          }
          
          setSelectedVariant(targetVariant);
          
          // Inizializza le opzioni selezionate con i valori della variante selezionata
          const initialOptions = targetVariant.options.reduce((acc, opt) => {
            acc[opt.option_name] = opt.option_value;
            return acc;
          }, {});
          setSelectedOptions(initialOptions);
        }
        setLoading(false);
        
        // Traccia la visualizzazione del prodotto
        if (response.data.product) {
          // Google Analytics
          trackProductView(response.data.product);
          // Business Analytics
          trackBusinessProductView(response.data.product);
        }
      } catch (err) {
        console.error('Errore durante il recupero del prodotto:', err);
        // Se l'errore è 404 (prodotto non trovato), imposta il flag
        if (err.response && err.response.status === 404) {
          setProductNotFound(true);
        } else {
          setError('Errore nel caricamento del prodotto');
        }
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, searchParams]);

  // Reset state on product id change (for ProductSlider navigation)
  useEffect(() => {
    setProduct(null);
    setVariants([]);
    setSellers([]);
    setSelectedVariant(null);
    setSelectedOptions({});
    setShowFullDescription(false);
    setLoading(true);
    setError(null);
  }, [id]);

  // Load shipping rates when product or sellers change
  useEffect(() => {
    if (product || sellers.length > 0) {
      const variantIds = [];
      
      // Aggiungi la variante selezionata o la prima variante del prodotto principale
      if (product && product.id && variants.length > 0) {
        variantIds.push(selectedVariant?.id || variants[0].id);
      }
      
      // Aggiungi le prime varianti dei sellers
      sellers.forEach(sellerData => {
        if (sellerData.product_id && sellerData.variants && sellerData.variants.length > 0) {
          variantIds.push(sellerData.variants[0].id);
        }
      });
      
      if (variantIds.length > 0) {
        loadShippingRates(variantIds);
      }
    }
  }, [product, sellers, variants, selectedVariant, loadShippingRates]);

  if (loading) {
    return <ProductPageSkeleton />;
  }

  // Se il prodotto non è stato trovato, reindirizza alla pagina 404
  if (productNotFound) {
    return <Navigate to="/404" replace />;
  }

  if (error) {
    return (
      <div className="main-content-container">
        <div className="product-page-error">
          <div className="product-page-error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return <Navigate to="/404" replace />;
  }

  const groupedVariants = groupVariantsByOption(variants);

  // SEO Metadata per il prodotto
  const seoData = product ? generateProductSEOMetadata(product, sellers) : null;

  return (
    <>
      {seoData && <SEOMetadata {...seoData} />}
      <div className="main-content-container product-page">
      <div className="product-page-content">
        <div className="product-page-grid">
          {/* Colonna sinistra - Immagine */}
          <div className="product-image-column">
            <div className="product-image-card">
              <div
                className="product-image-zoomable"
                onClick={() => setZoomOpen(true)}
              >
                <img
                  src={selectedVariant?.variant_image || product.main_image_url}
                  alt={product.title}
                />
                <span className="product-image-zoom-hint">Zoom</span>
              </div>
            </div>
            {/* Modal zoom immagine */}
            {zoomOpen && (
              <div className="product-image-zoom-modal" onClick={()=>setZoomOpen(false)}>
                <button 
                  className="product-image-zoom-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomOpen(false);
                  }}
                  aria-label="Chiudi zoom"
                >
                  ×
                </button>
                <img
                  src={selectedVariant?.variant_image || product.main_image_url}
                  alt={product.title}
                  onClick={e => e.stopPropagation()}
                />
              </div>
            )}
          </div>

          {/* Colonna destra - Dettagli prodotto e varianti */}
          <div className="product-details-column">
            <h1 className="product-title">{product.title}</h1>
            
            {/* Brand sotto il titolo */}
            {product.brand && (
              <div className="product-brand">
                <a href={`/brands/${encodeURIComponent(product.brand)}`} className="product-brand-link">
                  {product.brand}
                </a>
              </div>
            )}
            
            {/* Venditore principale migliorato */}
            {product.vendor && (
              <div className="product-vendor">
                <Link to={`/seller/${product.seller.seller_handle || ''}`} className="main-vendor-box">
                  <img src={product.seller.store_logo_square || '/favicon.png'} alt={product.seller.store_name} className="main-vendor-logo" />
                  <span className="main-vendor-name">{product.seller.store_name}</span>
                  <span className="main-vendor-badge">Venditore principale</span>
                </Link>
              </div>
            )}
            
            {/* Prezzo e disponibilità */}
            <div className="product-pricing">
              <div className="product-price-row">
                {priceDisplay}
                {compareAtDisplay}
                {discount && (
                  <span className="product-discount-badge">
                    -{discount}%
                  </span>
                )}
              </div>
              <span className={`product-availability-badge ${product.availability ? 'available' : 'unavailable'}`}>
                {product.availability ? "Disponibile" : "Non disponibile"}
              </span>
            </div>

            {/* Varianti raggruppate per option_name */}
            {Object.entries(groupedVariants)
              .filter(([optionName, optionValues]) =>
                !(optionName === 'Title' && optionValues.length === 1 && optionValues[0] === 'Default Title')
              )
              .map(([optionName, optionValues]) => (
                <div key={optionName} className="product-variant-section">
                  <h4 className="product-variant-title">
                    {optionName}
                    {isColorOption(optionName) && selectedOptions[optionName] && (
                      <span className="product-variant-selected">{selectedOptions[optionName]}</span>
                    )}
                  </h4>
                  <div className="product-variant-options">
                    {optionValues
                      .filter(value => !(optionName === 'Title' && value === 'Default Title'))
                      .map((value) => {
                        const isSelected = selectedOptions[optionName] === value;
                        const isDisabled = !isCombinationAvailable(optionName, value);
                        return (
                          <div
                            key={value}
                            className={`product-variant-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${isColorOption(optionName) ? 'color-option' : ''}`}
                            onClick={() => {
                              if (isDisabled) return;
                              // Aggiorna le opzioni selezionate
                              const newOptions = { ...selectedOptions, [optionName]: value };
                              setSelectedOptions(newOptions);
                              // Trova la variante corrispondente
                              const newVariant = findVariantByOptions(newOptions);
                              setSelectedVariant(newVariant);
                              // Aggiorna l'URL con il parametro variant
                              if (newVariant) {
                                updateUrlWithVariant(newVariant.id);
                              }
                            }}
                          >
                            {isColorOption(optionName) ? (
                              <img
                                src={getColorImage(value)}
                                alt={value}
                                className="product-variant-color-image"
                              />
                            ) : (
                              <div className="product-variant-text">{value}</div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}

            {/* EAN prima della descrizione */}
            {eanToShow && (
              <div className="product-ean">
                <strong>EAN:</strong> {eanToShow}
              </div>
            )}

            {/* Informazioni di spedizione */}
            <div className="product-shipping-section">
              <h4 className="product-shipping-title">Spedizione</h4>
              <div className="product-shipping-info">
                {formatShippingInfo(selectedVariant?.id || variants[0]?.id)}
              </div>
            </div>

            {/* Descrizione */}
            <div className="product-description-section">
              <h4 className="product-description-title">Descrizione</h4>
              <div className="product-description-text">
                {isLong && !showFullDescription ? (
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(shortDescription) }} />
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(descriptionText) }} />
                )}
              </div>
              {isLong && (
                <button
                  className="product-description-toggle"
                  onClick={() => setShowFullDescription(v => !v)}
                >
                  {showFullDescription ? 'Mostra meno' : 'Mostra di più'}
                </button>
              )}
            </div>

            {/* Pulsanti azione */}
            <div className="product-actions">
              <a
                className="product-buy-button"
                href={addUtmParams(selectedVariant?.variant_link_to_website || product.main_product_url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Acquista sul sito del partner
              </a>
            </div>
            
            <div className="product-share">
              <span className="product-share-label">Condividi:</span>
              <div className="product-share-buttons">
                <a
                  href={`mailto:?subject=${encodeURIComponent(product.title)}&body=${encodeURIComponent(window.location.href)}`}
                  target="_blank" rel="noopener noreferrer"
                  title="Condividi via Email"
                  className="product-share-button email"
                >
                  <FaEnvelope />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank" rel="noopener noreferrer"
                  title="Condividi su Facebook"
                  className="product-share-button facebook"
                >
                  <FaFacebook />
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(product.title + ' ' + window.location.href)}`}
                  target="_blank" rel="noopener noreferrer"
                  title="Condividi su WhatsApp"
                  className="product-share-button whatsapp"
                >
                  <FaWhatsapp />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Lista dei seller a tutta larghezza */}
        <div className="product-sellers-section">
          <h4 className="product-sellers-title">Disponibile da questi venditori</h4>
          <div className="product-sellers-table">
            <div className="product-sellers-header">
              <div className="product-sellers-header-cell">Venditore</div>
              <div className="product-sellers-header-cell">Prezzo</div>
              <div className="product-sellers-header-cell">Spedizione</div>
              <div className="product-sellers-header-cell"></div>
            </div>
            <div className="product-sellers-body">
              {sellers.map((sellerData) => {
                // Logica prezzo/sconto coerente
                const showCompareAt = sellerData.compare_at_price_min && sellerData.compare_at_price_min > sellerData.price;
                const discount = showCompareAt ? Math.round(((sellerData.compare_at_price_min - sellerData.price) / sellerData.compare_at_price_min) * 100) : null;
                return (
                  <div key={sellerData.id} className={`product-seller-row ${sellerData.isMain ? 'main-seller' : ''}`}>
                    <div className="product-seller-info">
                      <img 
                        src={sellerData.seller.store_logo_square} 
                        alt={sellerData.seller.store_name}
                        className="product-seller-logo"
                      />
                      <div className="product-seller-details">
                        <Link to={`/seller/${sellerData.seller.seller_handle || ''}`} className="product-seller-name">
                          {sellerData.seller.store_name}
                        </Link>
                        {sellerData.isMain && (
                          <div className="product-seller-badge">Venditore principale</div>
                        )}
                      </div>
                    </div>
                    <div className="product-seller-price">
                      <span className="product-seller-price-main">
                        €{parseFloat(sellerData.price).toFixed(2)}
                      </span>
                      {showCompareAt && (
                        <span className="product-seller-price-compare">
                          €{parseFloat(sellerData.compare_at_price_min).toFixed(2)}
                        </span>
                      )}
                      {discount && (
                        <span className="product-seller-discount">-{discount}%</span>
                      )}
                    </div>
                    <div className="product-seller-shipping">
                      {formatShippingInfo(sellerData.variants?.[0]?.id || sellerData.product_id)}
                    </div>
                    <div className="product-seller-actions">
                      <a 
                        className="product-seller-visit-btn"
                        href={addUtmParams(sellerData.main_product_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Acquista
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Product Slider by Type */}
        {product?.product_type && (
          <ProductSlider 
            productType={product.product_type}
            excludeId={product.id}
            sliderTitle={`Potrebbe piacerti anche`}
          />
        )}
      </div>
    </div>
    </>
  );
};

export default ProductPage; 